package at.apa.statistictracker.data.remote

import at.apa.statistictracker.configuration.Configuration
import io.ktor.client.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.serialization.json.Json
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.JsonElement
import io.ktor.client.plugins.contentnegotiation.*

class StatisticTrackerApiService(
    configuration: Configuration,
) {

    private val baseUrl = configuration.endpointConfig.apiBaseUrl
    private val client = createHttpClient(
        configuration.endpointConfig.apiBaseUrl,
        configuration.endpointConfig.apiKey
    )

    /**
     * Posts a statistic event to the remote API.
     *
     * @param jsonPayload the statistic event to be sent serialized in JSON format.
     * @throws StatisticTrackerApiException if the HTTP response code is not equal to 2xx.
     */
    suspend fun postEvent(jsonPayload: JsonElement) =
        client.post(baseUrl) {
            path("/api/events/save")
            json()
            setBody(jsonPayload)
        }

    /**
     * Posts a list of statistic events to the remote API.
     *
     * @param jsonPayloads a list of statistic events to be sent serialized in JSON format.
     * @throws
     * if the HTTP response code is not equal to 2xx.
     */
    suspend fun postEvents(jsonPayloads: List<JsonElement>) =
        client.post(baseUrl) {
            path("/api/events/saveAll")
            json()
            setBody(jsonPayloads)
        }

    private fun createHttpClient(baseUrl: String, apiKey: String) = HttpClient {
        HttpResponseValidator {
            handleResponseException { exception ->
                val clientException =
                    exception as? ClientRequestException ?: return@handleResponseException
                val exceptionResponse = exception.response

                when (exceptionResponse.status.value) {
                    404 -> throw StatisticTrackerApiException.NotFound
                    403 -> throw StatisticTrackerApiException.Forbidden
                    422 -> throw StatisticTrackerApiException.ValidationError(clientException.message)
                    else -> throw StatisticTrackerApiException.GeneralError(clientException.message)
                }
            }
        }

        defaultRequest {
            header("x-api-key", apiKey)
        }

        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
            })
        }

        install(Logging) {
            logger = object : Logger {
                override fun log(message: String) {
                    at.apa.statistictracker.logger.Logger.d(TAG, message)
                }
            }
            level = LogLevel.BODY
        }
    }

    private fun HttpRequestBuilder.json() {
        contentType(ContentType.Application.Json)
    }

    private fun HttpRequestBuilder.path(path: String) {
        url {
            // do not use += here, otherwise webpack cannot process the created javascript when the project is compiled for web
            encodedPath = encodedPath + path
        }
    }

    companion object {
        private const val TAG = "StatisticTrackerApiService"
    }
}